import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import { useApp } from '../context/AppContext';
import { apiClient } from '../api';
import { showToast } from '../utils/toaster';
import { generateRandomPath } from '../utils/stringUtils';
import { ChannelProtocol } from '../types';
import { GatewayChannel } from './EditChannelPage/types';
import IdentityDetailsModal from './IdentityDetailsModal';
import MonitoringTab from './EditChannelPage/tabs/MonitoringTab';
import OverviewTab from './EditChannelPage/tabs/OverviewTab';
import RoutingTab from './EditChannelPage/tabs/RoutingTab';
import InboundTab from './EditChannelPage/tabs/InboundTab';
import OutboundTab from './EditChannelPage/tabs/OutboundTab';
import IdentitiesTab from './EditChannelPage/tabs/IdentitiesTab';
import PoliciesTab from './EditChannelPage/tabs/PoliciesTab';
import NetworkingTab from './EditChannelPage/tabs/NetworkingTab';
import McpSandbox from './McpSandbox';
import { useRequestSchema } from './EditChannelPage/hooks/useRequestSchema';
import { useResponseSchema } from './EditChannelPage/hooks/useResponseSchema';
import { ChannelFormData } from './EditChannelPage/types';
import { DeleteButton } from './shared/DeleteButton';
import './SchemaEditor.css';
import './OnboardWizard.css';

const EditChannelPage: React.FC = () => {
  const { configId } = useParams<{ configId: string }>();
  const { state, actions, getCurrentStats } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState<ChannelFormData>({
    name: '',
    config_id: '',
    description: '',
    listen_address: '',
    target_endpoint: '',
    protocol: 'a2a',       // Default to A2A
    endpoint_type: 'url',  // Default to URL
    enabled: true,
    enforce_extension: false,
    enforce_response_extension: false,
  });
  // Note: schemaJson and responseSchemaJson are now managed by custom hooks
  // const [schemaJson, setSchemaJson] = useState<string>('');
  // const [responseSchemaJson, setResponseSchemaJson] = useState<string>('');
  const [isModified, setIsModified] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const [gateways, setGateways] = useState<Array<{ id: string; name: string; description: string; did: string; gateway_type: 'self' | 'remote'; status: 'active' | 'disabled' }>>([]);
  const [gatewayChannels, setGatewayChannels] = useState<GatewayChannel[]>([]);  // Channels from selected gateway
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);     // Loading state for channels
  const [isRefreshingChannels, setIsRefreshingChannels] = useState(false); // Loading state for cache refresh
  const [secrets, setSecrets] = useState<Array<{ id: string; name: string; secret_id: string; description?: string }>>([]);  // Secrets
  const [mediators, setMediators] = useState<Array<{ id: string; name: string; description: string; did: string; status?: string }>>([]);  // Mediators for MPX channels
  const [mpxTenants, setMpxTenants] = useState<Array<{ id: string; name: string; description: string; did: string; enabled: boolean }>>([]);  // MPX tenants
  const [openSecretsDropdown, setOpenSecretsDropdown] = useState<number | null>(null);  // Track which secrets dropdown is open
  
  // Display-only state for showing gateway/channel names (not part of formData, doesn't trigger isModified)
  const [displayGatewayName, setDisplayGatewayName] = useState<string>('');
  const [displayChannelName, setDisplayChannelName] = useState<string>('');
  
  // Cache for channel names (channelId -> name) to avoid repeated fetches
  const [channelNameCache, setChannelNameCache] = useState<Map<string, string>>(new Map());
  
  // Use ref to track if we're in initial route setup (to avoid triggering isModified)
  const isInitialRouteSetup = useRef<boolean>(false);
  
  // Track which gateway we've loaded channels for (to prevent duplicate loads)
  const loadedGatewayRef = useRef<string | undefined>(undefined);

  // Function to refresh gateway channel cache
  const refreshGatewayChannelsCache = async () => {
    setIsRefreshingChannels(true);
    try {
      const response = await fetch('/api/v1/gateways/refresh-channels', {
        method: 'POST',
      });
      const data = await response.json();
      
      // if (data.success) {
      //   console.log('Gateway channel cache refreshed:', data);
      //   setMessage({ type: 'success', text: `Refreshed ${data.refreshed.length} gateway(s)` });
        
      //   // Reload channels for currently selected gateway
      //   if (formData.gateway_id) {
      //     loadedGatewayRef.current = undefined; // Reset to allow reload
      //     // Trigger reload by updating a dummy state
      //     setIsLoadingChannels(true);
      //     setTimeout(() => setIsLoadingChannels(false), 100);
      //   }
      // } else {
        if (!data.success) {
         setMessage({ type: 'error', text: 'Failed to refresh gateway channels' });
        }
      // }
    } catch (error) {
      console.error('Failed to refresh gateway channel cache:', error);
      setMessage({ type: 'error', text: 'Failed to refresh gateway channels' });
    } finally {
      setIsRefreshingChannels(false);
    }
  };
  
  // Channel routing configuration state
  const [routingConfig, setRoutingConfig] = useState<{ available_listen_addresses: string[]; channel_path_prefix: Array<{id: string; name: string; prefix: string}> } | null>(null);
  const [selectedHostPort, setSelectedHostPort] = useState<string>('');
  const [selectedPrefix, setSelectedPrefix] = useState<string>('');
  const [customPath, setCustomPath] = useState<string>('');
  const [routeInitialized, setRouteInitialized] = useState<string | null>(null); // Track which channel's route we've initialized


  // Schema properties for visual editor
  interface SchemaProperty {
    name: string;
    type: string;
    required: boolean;
    xIdentity: boolean;
    level: number;
    parent?: number;
    collapsed?: boolean;
    value?: string; // For metadata properties - stores the actual value as string
    validationRule?: {
      type: string;
      value: string;
      elementType?: string;
      values?: string;
      min?: string;
      max?: string;
    };
  }
  
  // Note: Schema state is now managed by custom hooks (useRequestSchema and useResponseSchema)
  // These are initialized after parseSchemaToProperties is defined below

  // OPA policy validation state (actual policy stored in formData.opa_policy_config)
  const [opaPolicyValid, setOpaPolicyValid] = useState(true);
  const [opaPolicyErrors, setOpaPolicyErrors] = useState<string[]>([]);
  const [isValidatingPolicy, setIsValidatingPolicy] = useState(false);
  
  // MCP Tool Policy editor state (actual policies stored in formData.mcp_tool_policies_config.policies)
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
  const [showPolicyEditor, setShowPolicyEditor] = useState(false);
  const [policyName, setPolicyName] = useState('');
  const [policyDescription, setPolicyDescription] = useState('');
  const [policyCode, setPolicyCode] = useState('');
  const [policyEnforce, setPolicyEnforce] = useState(true);
  const [policyPriority, setPolicyPriority] = useState(100);
  const [isValidatingToolPolicy, setIsValidatingToolPolicy] = useState(false);
  const [toolPolicyValid, setToolPolicyValid] = useState(true);
  const [toolPolicyErrors, setToolPolicyErrors] = useState<string[]>([]);

  // Determine if this is create mode (before using in useMemo)
  const isCreateMode = configId === 'new';
  
  // Protocol selection step (only for create mode)
  const [protocolSelected, setProtocolSelected] = useState(!isCreateMode);
  
  const handleProtocolSelect = (protocol: ChannelProtocol) => {
    setFormData(prev => ({ ...prev, protocol }));
    setProtocolSelected(true);
  };

  // Compute full route for display - useMemo ensures this updates when dependencies change
  const fullRouteDisplay = useMemo(() => {
    if (selectedHostPort && selectedPrefix && customPath && customPath.trim()) {
      const normalizedPath = customPath.startsWith('/') ? customPath.substring(1) : customPath;
      const normalizedPrefix = selectedPrefix.startsWith('/') ? selectedPrefix.substring(1) : selectedPrefix;
      return `${selectedHostPort}/${normalizedPrefix}/${normalizedPath}`;
    }
    return 'Loading...';
  }, [selectedHostPort, selectedPrefix, customPath, routeInitialized, configId, isCreateMode]);

  // Fetch available gateways for gateway selector
  useEffect(() => {
    const fetchGateways = async () => {
      try {
        // Note: Cache refresh is handled by App.tsx on authentication
        // Manual refresh available via refreshGatewayChannelsCache() if needed

        const response = await apiClient.get('/gateways');
        // Filter to only show user-created gateways (exclude system-created)
        // System gateways are created when remote parties accept our connection points
        // User gateways are created when we accept OOB invitations from others
        const userGateways = response.data.filter((gw: any) => 
          gw.gateway_type === 'remote' && gw.creation_type === 'user'
        );
        setGateways(userGateways);
      } catch (error) {
        console.error('Failed to fetch gateways:', error);
      }
    };
    fetchGateways();
  }, []);

  // Fetch available mediators for MPX channel setup
  useEffect(() => {
    const fetchMediators = async () => {
      try {
        const response = await apiClient.get('/mediators/compatible');
        const mediatorsList = Array.isArray(response.data) ? response.data : [];
        setMediators(mediatorsList);
      } catch (error) {
        console.error('Failed to fetch mediators:', error);
      }
    };
    fetchMediators();
  }, []);

  // Fetch MPX tenants for MPX channel configuration display
  useEffect(() => {
    const fetchMpxTenants = async () => {
      try {
        const response = await apiClient.get('/mpx-tenants');
        const tenantsList = Array.isArray(response.data) ? response.data : [];
        setMpxTenants(tenantsList);
      } catch (error) {
        console.error('Failed to fetch MPX tenants:', error);
      }
    };
    fetchMpxTenants();
  }, []);

  // Fetch secrets for metadata value selection
  useEffect(() => {
    const fetchSecrets = async () => {
      try {
        const response = await fetch('/api/v1/secrets/');
        if (response.ok) {
          const secrets = await response.json();
          //console.log('Loaded secrets:', secrets);
          setSecrets(secrets);
        } else {
          console.error('Failed to fetch secrets, status:', response.status, await response.text());
        }
      } catch (error) {
        console.error('Failed to fetch secrets:', error);
      }
    };
    fetchSecrets();
  }, []);

  // Helper function to parse fabric:// URLs and get gateway/channel IDs
  const parseFabricUrl = (endpoint: string): { gatewayId?: string; channelId?: string } | null => {
    if (!endpoint.startsWith('fabric://')) {
      return null;
    }
    
    const fabricUrl = endpoint.substring(9); // Remove 'fabric://'
    const parts = fabricUrl.split('/');
    if (parts.length >= 2) {
      return {
        gatewayId: parts[0],
        channelId: parts[1]
      };
    }
    return null;
  };

  // Helper function to detect if channel is UCP
  const isUcpChannel = (channelData: any): boolean => {
    const ucpExtension = 'https://ucp.dev/specification/reference?v=2026-01-11';
    return channelData?.primary_extension === ucpExtension || 
           channelData?.supported_extensions?.includes(ucpExtension);
  };

  // Helper function to get display protocol (shows UCP if it's a UCP channel)
  const getDisplayProtocol = (channelData: any): string => {
    if (isUcpChannel(channelData)) {
      return 'UCP';
    }
    return channelData?.protocol?.toUpperCase() || 'A2A';
  };

  // Helper function to get gateway and channel display names

  // Fetch routing configuration
  useEffect(() => {
    const fetchRoutingConfig = async () => {
      try {
        const response = await fetch('/api/v1/config/channel-routing');
        if (!response.ok) {
          throw new Error('Failed to fetch routing configuration');
        }
        const config = await response.json();
        setRoutingConfig(config);
        
        // Set random custom path for new channels if not already set
        if (!configId && !customPath) {
          setCustomPath(generateRandomPath());
        }
      } catch (error: any) {
        console.error('Failed to fetch routing configuration:', error);
      }
    };
    fetchRoutingConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]); // Only re-fetch when configId changes (switching between create/edit mode)

  // Update listen_address when dropdown selections change
  useEffect(() => {
    if (selectedHostPort && selectedPrefix && customPath) {
      const hostPort = selectedHostPort.replace(/^https?:\/\//, '');
      const normalizedPath = customPath.startsWith('/') ? customPath : `/${customPath}`;
      
      setFormData(prev => ({
        ...prev,
        listen_address: hostPort,
      }));
      
      // Only mark as modified if not during initial route setup
      if (!isInitialRouteSetup.current) {
        setIsModified(true);
      }
    }
  }, [selectedHostPort, selectedPrefix, customPath]);

  // Get the current stats (will be filtered when on monitoring tab)
  // Use useMemo to ensure we react to state changes properly
  const stats = useMemo(() => {
    const currentStats = state.activeView === 'global' 
      ? state.globalState.stats 
      : state.filteredState.stats;
    return currentStats;
  }, [state.activeView, state.globalState.stats, state.filteredState.stats]);

  // Monitoring tab - use filtered dashboard stats from AppContext
  // Channel metrics are now derived from stats (filtered by channel_id)
  // The AppContext handles WebSocket updates automatically
  const channelMetrics = useMemo(() => {
    if (!stats?.metrics?.time_series || !stats?.metrics?.latency_time_series) {
      return null;
    }
    
    return {
      channel_config_id: formData.config_id || configId || '',
      time_series: stats.metrics.time_series.map(point => ({
        timestamp: point.timestamp,
        count: point.count || 0,
        rule_accepts: point.rule_accepts || 0,
        rule_denies: point.rule_denies || 0,
        gateway_faults: point.gateway_faults || 0,
      })),
      latency_time_series: stats.metrics.latency_time_series.map(point => ({
        timestamp: point.timestamp,
        avg_latency_ms: point.avg_latency_ms || 0,
        p50_latency_ms: point.p50_latency_ms || 0,
        p95_latency_ms: point.p95_latency_ms || 0,
        p99_latency_ms: point.p99_latency_ms || 0,
        min_latency_ms: point.min_latency_ms || 0,
        max_latency_ms: point.max_latency_ms || 0,
        sample_count: point.sample_count || 0,
      })),
    };
  }, [stats, formData.config_id, configId]);
  
  // Active tab state - read from URL parameter if present (must be declared before using in useEffect)
  const defaultTab = isCreateMode ? 'basic' : 'glance';
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(tabFromUrl || defaultTab);

  // Set dashboard filters when on a channel page (for background data loading)
  useEffect(() => {
    if (!configId || configId === 'new') {
      // Clear filters for new channel creation
      actions.setDashboardFilters(null);
      return;
    }
    
    // Always set filter for this channel (to preload data in background)
    // But only if it's not already set to avoid unnecessary resets
    if (state.dashboardFilters?.channelId !== configId) {
      actions.setDashboardFilters({ channelId: configId });
    }
    
    // Don't clear filters on unmount - the next page will set its own filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]); // Only depend on configId, not activeTab

  // Switch active view based on which tab is displayed
  useEffect(() => {
    if (!configId || configId === 'new') {
      return;
    }
    
    // When on a channel page, always use filtered view (all tabs need channel-specific data)
    // The filter is set above, so the data is filtered to this channel
    actions.setActiveView('filtered');
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, configId]); // Switch view when tab changes

  // Trigger load when filter is set AND timestamp is reset
  useEffect(() => {
    const activeStore = state.activeView === 'global' ? state.globalState : state.filteredState;
    const shouldLoad = state.dashboardFilters?.channelId === configId && activeStore.lastSyncTimestamp === null && configId && configId !== 'new';
    
    if (shouldLoad) {
      actions.loadDashboardStats(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dashboardFilters, state.activeView, state.globalState.lastSyncTimestamp, state.filteredState.lastSyncTimestamp, configId]);

  // Use common toaster utility with channel-specific options
  const showChannelToast = (type: 'success' | 'error' | 'loading', text: string) => {
    const duration = type === 'error' ? 8000 : (type === 'success' ? 2000 : 10000);
    showToast(type, text, { autoRemove: type !== 'loading', duration });
  };

  // Track if form has been loaded initially
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  
  // Track if we're currently loading initial data (to prevent marking as modified)
  const isLoadingInitialData = useRef(false);

  // Find the channel by config_id from dashboard stats
  const channel = stats?.channels?.find(r => r.config_id === configId);

  // Update URL when tab changes
  const handleTabChange = (tab: string | null) => {
    if (tab) {
      setActiveTab(tab);
      setSearchParams({ tab }, { replace: true });
    }
  };

  // Identity modal state
  const [showIdentityModal, setShowIdentityModal] = useState(false);
  const [selectedIdentity, setSelectedIdentity] = useState<any>(null);
  const [didDocumentContent, setDidDocumentContent] = useState<any>(null);
  const [loadingDidDocument, setLoadingDidDocument] = useState(false);

  // Helper functions for identity modal

  const loadDidDocumentForIdentity = async (did: string) => {
    setLoadingDidDocument(true);
    try {
      const response = await fetch(`/api/v1/identity/resolve-did?did=${encodeURIComponent(did)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch DID document: ${response.status}`);
      }
      const didDoc = await response.json();
      setDidDocumentContent(didDoc);
    } catch (error) {
      console.error('Error loading DID document:', error);
      setDidDocumentContent({ error: 'Failed to load DID document' });
    } finally {
      setLoadingDidDocument(false);
    }
  };


  // Load DID document when identity modal opens
  useEffect(() => {
    if (showIdentityModal && selectedIdentity && !didDocumentContent && !loadingDidDocument) {
      loadDidDocumentForIdentity(selectedIdentity.did);
    }
  }, [showIdentityModal, selectedIdentity, didDocumentContent, loadingDidDocument]);

  // Generate a random port in the user-space range (3000-9999)
  const generateRandomPort = (): number => {
    return Math.floor(Math.random() * (9999 - 3000 + 1)) + 3000;
  };

  // Reset modified state when navigating to a different channel
  useEffect(() => {
    // When configId changes, reset the modified flag and form initialization
    setIsModified(false);
    setIsFormInitialized(false);
    isLoadingInitialData.current = false;
  }, [configId]);

  // Load channel data on mount, but don't overwrite if user is editing
  useEffect(() => {
    // Reset route initialization flag when navigating to a different channel
    // Only reset if user is not currently editing
    if (!isModified) {
      setRouteInitialized(null);
    }
    
    if (isCreateMode) {
      // Initialize with default values for new channel
      if (!isFormInitialized) {
        isLoadingInitialData.current = true;
        const uuid = crypto.randomUUID();
        setFormData(prev => ({
          name: `new-channel-${Date.now()}`,
          config_id: uuid,
          description: 'New channel',
          listen_address: `127.0.0.1:${generateRandomPort()}`,
          target_endpoint: 'https://httpbin.org/anything',
          endpoint_type: 'url',  // Default to URL endpoint
          enforce_extension: false,
          enforce_response_extension: false,
          enabled: true,
          protocol: prev.protocol || 'a2a', // Preserve protocol if already selected, otherwise default to a2a
        }));
        
        // Set random custom path for new channels
        setCustomPath(generateRandomPath());
        
        const defaultSchema = {
          "properties": {
            "agentIdentity": {
              "properties": {
              },
              "required": [],
              "type": "object"
            }
          },
          "required": [],
          "type": "object"
        };
        setSchemaJson(JSON.stringify(defaultSchema, null, 2));
        setResponseSchemaJson(JSON.stringify(defaultSchema, null, 2));
        setIsFormInitialized(true);
        setIsModified(false);
        // Clear the loading flag immediately after setting form data
        isLoadingInitialData.current = false;
      }
    } else if (configId && stats?.channels) {
      const channel = stats.channels.find(r => r.config_id === configId);
      if (channel) {
        // Only update form data if:
        // 1. Form hasn't been initialized yet (initial load), OR
        // 2. User hasn't made any modifications (not currently editing)
        // This allows the form to reload after save when dashboard data refreshes
        if (!isFormInitialized || !isModified) {
          isLoadingInitialData.current = true;
          // Parse endpoint type from target_endpoint
          let endpoint_type: 'url' | 'gateway' | 'mcp-proxy' = 'url';
          let gateway_id: string | undefined;
          let gateway_channel: string | undefined;
          let mcp_proxy_id: string | undefined;
          
          // Parse gateway reference format: fabric://{gateway_id}/{channel_id}
          if (channel.target_endpoint.startsWith('fabric://')) {
            endpoint_type = 'gateway';
            const fabricUrl = channel.target_endpoint.substring(9); // Remove 'fabric://'
            const parts = fabricUrl.split('/');
            if (parts.length >= 2) {
              gateway_id = parts[0];
              gateway_channel = parts[1];
            }
          } else if (channel.target_endpoint.startsWith('did:gateway:')) {
            // Legacy format support: did:gateway:{gateway_id}:{channel_name}
            endpoint_type = 'gateway';
            const parts = channel.target_endpoint.split(':');
            if (parts.length >= 4) {
              gateway_id = parts[2];
              gateway_channel = parts.slice(3).join(':');
            }
          } else if (channel.target_endpoint.startsWith('proxy://')) {
            // MCP proxy backend: proxy://{proxy_id}
            endpoint_type = 'mcp-proxy';
            mcp_proxy_id = channel.target_endpoint.substring(8); // Remove 'proxy://'
          }
          
          setFormData({
            name: channel.name,
            config_id: channel.config_id,
            description: channel.description || '',
            listen_address: channel.listen_address,
            target_endpoint: channel.target_endpoint,
            endpoint_type,
            gateway_id,
            gateway_channel,
            mcp_proxy_id,
            enabled: channel.enabled !== undefined ? channel.enabled : true,
            enforce_extension: channel.enforce_extension || false,
            enforce_response_extension: channel.enforce_response_extension || false,
            primary_extension: (channel as any).primary_extension,
            supported_extensions: (channel as any).supported_extensions || [],
            extension_rules: channel.extension_rules,
            response_extension_rules: channel.response_extension_rules,
            custom_metadata: channel.custom_metadata,
            response_custom_metadata: (channel as any).response_custom_metadata,
            mcp_identity_config: (channel as any).mcp_identity_config || { enabled: false, meta_field: 'agentIdentity' },
            protocol: channel.protocol || 'a2a',
            mpx_tenant_id: (channel as any).mpx_tenant_id,
            mpx_device_token: (channel as any).mpx_device_token,
            mpx_mediator_id: (channel as any).mpx_mediator_id,
            mpx_custom_phrase: (channel as any).mpx_custom_phrase,
            opa_policy_config: (channel as any).opa_enabled || (channel as any).opa_policy 
              ? { enabled: (channel as any).opa_enabled || false, policy: (channel as any).opa_policy || '' }
              : undefined,
            mcp_tool_policies_config: (channel as any).mcp_tool_policies_enabled !== undefined || ((channel as any).mcp_tool_policies && Array.isArray((channel as any).mcp_tool_policies))
              ? { 
                  enabled: (channel as any).mcp_tool_policies_enabled || false, 
                  policies: (channel as any).mcp_tool_policies && Array.isArray((channel as any).mcp_tool_policies) ? (channel as any).mcp_tool_policies : []
                }
              : undefined,
            rate_limit: (channel as any).rate_limit,
            timeout: (channel as any).timeout,
            retry: (channel as any).retry,
            circuit_breaker: (channel as any).circuit_breaker,
            mirror: (channel as any).mirror,
            publish_to_did_document: (channel as any).publish_to_did_document || false,
          });

        // Set schema JSON strings
        const initialSchemaJson = channel.extension_rules?.json_schema ? JSON.stringify(channel.extension_rules.json_schema, null, 2) : '';
        const initialResponseSchemaJson = channel.response_extension_rules?.json_schema ? JSON.stringify(channel.response_extension_rules.json_schema, null, 2) : '';
        const initialMetadataSchemaJson = channel.custom_metadata?.payload ? JSON.stringify(channel.custom_metadata.payload, null, 2) : '{}';
        const initialResponseMetadataSchemaJson = (channel as any).response_custom_metadata?.payload ? JSON.stringify((channel as any).response_custom_metadata.payload, null, 2) : '{}';
        
        setSchemaJson(initialSchemaJson);
        setResponseSchemaJson(initialResponseSchemaJson);
        setMetadataSchemaJson(initialMetadataSchemaJson);
        setResponseMetadataSchemaJson(initialResponseMetadataSchemaJson);
        
        // Initialize the last synced values to prevent auto-sync from triggering modification
        setLastSyncedSchemaJson(initialSchemaJson);
        setLastSyncedResponseSchemaJson(initialResponseSchemaJson);
        setLastSyncedMetadataSchemaJson(initialMetadataSchemaJson);
        setLastSyncedResponseMetadataSchemaJson(initialResponseMetadataSchemaJson);
        
        // Populate visual editors from JSON on initial load
        if (initialSchemaJson) {
          try {
            const parsedProperties = parseSchemaToProperties(initialSchemaJson);
            setSchemaProperties(parsedProperties);
          } catch (e) {
            // Failed to parse initial schema JSON
          }
        }
        
        if (initialResponseSchemaJson) {
          try {
            const parsedResponseProperties = parseSchemaToProperties(initialResponseSchemaJson);
            setResponseSchemaProperties(parsedResponseProperties);
          } catch (e) {
            // Failed to parse initial response schema JSON
          }
        }

        setIsFormInitialized(true);
        setIsModified(false);
        // Clear the loading flag immediately after setting form data
        isLoadingInitialData.current = false;
        }
      }
    }
  }, [configId, isFormInitialized, isCreateMode, routingConfig, stats]);

  // Resolve gateway and channel names for display (without triggering isModified)
  useEffect(() => {
    if (!formData.gateway_id || formData.endpoint_type !== 'gateway') {
      setDisplayGatewayName('');
      setDisplayChannelName('');
      return;
    }

    // Find gateway name from gateways list
    const gateway = gateways.find(g => g.id === formData.gateway_id);
    if (gateway) {
      setDisplayGatewayName(gateway.name);
    } else {
      setDisplayGatewayName('');
    }

    // Find channel name from already-loaded gatewayChannels (no queries)
    if (formData.gateway_channel) {
      const channel = gatewayChannels.find(c => c.config_id === formData.gateway_channel);
      if (channel) {
        setDisplayChannelName(channel.name);
      } else {
        // Channel not loaded yet or doesn't exist
        setDisplayChannelName('');
      }
    } else {
      setDisplayChannelName('');
    }
  }, [formData.gateway_id, formData.gateway_channel, formData.endpoint_type, gateways, gatewayChannels]);

  // Load gateway channels when editing an existing fabric channel OR creating a new one
  useEffect(() => {
    // Only run when we have a gateway_id selected and endpoint type is gateway
    if (!formData.gateway_id || formData.endpoint_type !== 'gateway') {
      return;
    }
    
    // In edit mode, wait for form to be initialized
    if (!isCreateMode && !isFormInitialized) {
      return;
    }

    // Don't reload if we've already loaded channels for this gateway
    if (loadedGatewayRef.current === formData.gateway_id) {
      return;
    }

    // Fetch channels from the gateway
    const loadGatewayChannels = async () => {
      setIsLoadingChannels(true);
      try {
        const response = await apiClient.get(`/gateways/${formData.gateway_id}/channels`);
        
        // Check if response has success and channels
        if (response.data && response.data.success && Array.isArray(response.data.channels)) {
          // Filter channels to match the current channel's protocol
          const currentProtocol = formData.protocol || 'a2a';
          const allChannels = response.data.channels.map((ch: any) => ({
            config_id: ch.config_id,
            name: ch.name,
            description: ch.description,
            protocol: ch.protocol || 'a2a'
          }));
          
          // Only show channels that match the current protocol (strict matching)
          const channels = allChannels.filter((ch: any) => {
            const chProtocol = ch.protocol || 'a2a';
            return chProtocol === currentProtocol;
          });
          setGatewayChannels(channels);

          // Update cache with channel names
          const newCache = new Map(channelNameCache);
          channels.forEach((ch: any) => {
            newCache.set(ch.config_id, ch.name);
          });
          setChannelNameCache(newCache);
          
          // Mark this gateway as loaded
          loadedGatewayRef.current = formData.gateway_id;
        } else {
          console.warn('Gateway returned unexpected response:', response.data);
          setGatewayChannels([]);
        }
      } catch (error: any) {
        // Silently handle errors for disconnected gateways
        if (error?.message?.includes('503') || error?.message?.includes('not connected')) {
          console.debug('Gateway is not connected - channel list unavailable');
        } else {
          console.error('Failed to load gateway channels:', error);
        }
        setGatewayChannels([]);
      } finally {
        setIsLoadingChannels(false);
      }
    };

    loadGatewayChannels();
  }, [formData.gateway_id, formData.endpoint_type, formData.protocol, isCreateMode, isFormInitialized]);

  // Initialize route dropdowns from channel data - runs ONCE when all data is ready
  useEffect(() => {
    // Skip if in create mode or already initialized for this channel
    if (isCreateMode || routeInitialized === configId) {
      return;
    }

    // Wait until we have ALL required data
    if (!configId || !stats?.channels || !routingConfig) {
      return;
    }

    const channel = stats.channels.find((r: any) => r.config_id === configId);
    if (!channel) {
      return;
    }

    // Don't initialize if channel doesn't have a route yet
    if (!channel.route) {
      return;
    }

    let parsedSuccessfully = false;
    
    // Set flag to indicate we're doing initial route setup
    isInitialRouteSetup.current = true;

    // The backend combines listen_address and route for display
    // We need to extract the actual host:port from the combined listen_address
    // Format: "host:port/prefix/path" or "host/prefix/path" (portless)
    let hostPort = channel.listen_address;
    
    // If listen_address contains a path (has /), extract just the host:port part
    if (channel.listen_address.includes('/')) {
      hostPort = channel.listen_address.split('/')[0];
    }

    // Parse and set listen address - match against the host:port part only
    const matchingAddress = routingConfig.available_listen_addresses.find(addr => {
      const addrWithoutProtocol = addr.replace(/^https?:\/\//, '');
      return addrWithoutProtocol === hostPort || addrWithoutProtocol.startsWith(hostPort + ':');
    });
    
    if (matchingAddress) {
      setSelectedHostPort(matchingAddress);
      parsedSuccessfully = true;
    }
    
    // Parse and set route prefix and custom path
    const channelRoute = channel.route;
    const routeParts = channelRoute.split('/').filter((p: string) => p);
    
    if (routeParts.length > 0) {
      const firstPart = routeParts[0];
      // Match against prefix without leading slash
      const matchingPrefix = routingConfig.channel_path_prefix.find(p => {
        const normalizedPrefix = p.prefix.startsWith('/') ? p.prefix.substring(1) : p.prefix;
        return normalizedPrefix === firstPart;
      });
      if (matchingPrefix) {
        const extractedPath = routeParts.slice(1).join('/');
        setSelectedPrefix(matchingPrefix.prefix);
        setCustomPath(extractedPath);
        parsedSuccessfully = true;
      } else {
        // Route doesn't match known prefixes, use default prefix and full route as path
        if (routingConfig.channel_path_prefix.length > 0) {
          setSelectedPrefix(routingConfig.channel_path_prefix[0].prefix);
        }
        const extractedPath = channelRoute.startsWith('/') ? channelRoute.substring(1) : channelRoute;
        setCustomPath(extractedPath);
        parsedSuccessfully = true;
      }
    }
    
    // Only mark as initialized if we successfully parsed something
    if (parsedSuccessfully) {
      setRouteInitialized(configId);
      // Clear the initial setup flag after a brief delay to allow effects to settle
      setTimeout(() => {
        isInitialRouteSetup.current = false;
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId, stats, routingConfig, routeInitialized]);

  // Metadata JSON parsing is now handled internally by the custom hooks
  // useRequestSchema and useResponseSchema manage their own metadata state

  // Helper function to count identity fields in a schema

  // Helper function to count all validation rules (both schema-based and custom rules)

  // Validation and input handling functions moved after hook initialization (see below)
  // to avoid using hook state before hooks are declared

  // Debounced OPA policy validation
  useEffect(() => {
    // Don't validate if OPA is disabled or policy is empty
    if (!formData.opa_policy_config?.enabled || !formData.opa_policy_config?.policy.trim()) {
      setOpaPolicyValid(true);
      setOpaPolicyErrors([]);
      return;
    }

    // Clear previous validation state
    setIsValidatingPolicy(true);

    // Debounce validation by 500ms
    const timer = setTimeout(async () => {
      try {
        const response = await fetch('/api/v1/channels/validate-policy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ policy: formData.opa_policy_config?.policy }),
        });

        const result = await response.json();
        
        setOpaPolicyValid(result.valid);
        if (!result.valid && result.error) {
          setOpaPolicyErrors([result.error]);
        } else {
          setOpaPolicyErrors([]);
        }
      } catch (error) {
        console.error('Policy validation failed:', error);
        setOpaPolicyErrors(['Failed to validate policy']);
        setOpaPolicyValid(false);
      } finally {
        setIsValidatingPolicy(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.opa_policy_config]);

  // Validate MCP tool policy in modal
  useEffect(() => {
    // Don't validate if policy is empty
    if (!policyCode.trim()) {
      setToolPolicyValid(true);
      setToolPolicyErrors([]);
      return;
    }

    // Clear previous validation state
    setIsValidatingToolPolicy(true);

    // Debounce validation by 500ms
    const timer = setTimeout(async () => {
      try {
        const response = await fetch('/api/v1/channels/validate-policy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ policy: policyCode }),
        });

        const result = await response.json();
        
        setToolPolicyValid(result.valid);
        if (!result.valid && result.error) {
          setToolPolicyErrors([result.error]);
        } else {
          setToolPolicyErrors([]);
        }
      } catch (error) {
        console.error('Tool policy validation failed:', error);
        setToolPolicyErrors(['Failed to validate policy']);
        setToolPolicyValid(false);
      } finally {
        setIsValidatingToolPolicy(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [policyCode]);


  // Helper function to parse schema - used by custom hooks
  const parseSchemaToProperties = useCallback((schemaText: string): SchemaProperty[] => {
    if (!schemaText.trim()) return [];
    
    try {
      const schema = JSON.parse(schemaText);
      const properties: SchemaProperty[] = [];
      
      const parseObject = (obj: any, level: number = 0, parentIndex?: number) => {
        if (obj.type === 'object' && obj.properties) {
          Object.keys(obj.properties).forEach(key => {
            const prop = obj.properties[key];
            const property: SchemaProperty = {
              name: key,
              type: prop.type || 'string',
              required: obj.required?.includes(key) || false,
              xIdentity: prop['x-identity'] || false,
              level,
              parent: parentIndex,
              collapsed: true
            };
            
            // Add validation rules
            if (prop.const !== undefined) {
              property.validationRule = { type: 'equals', value: prop.const.toString() };
            } else if (prop.enum && Array.isArray(prop.enum)) {
              property.validationRule = { type: 'oneof', value: prop.enum.join(',') };
            } else if (prop.pattern) {
              // Check if it's a contains pattern (.*value.*) or regex
              const containsMatch = prop.pattern.match(/^\.\.\*(.+)\.\.\*$/);
              if (containsMatch) {
                property.validationRule = { type: 'contains', value: containsMatch[1] };
              } else {
                property.validationRule = { type: 'regex', value: prop.pattern };
              }
            } else if (prop.minimum !== undefined || prop.maximum !== undefined) {
              const min = prop.minimum !== undefined ? prop.minimum.toString() : '';
              const max = prop.maximum !== undefined ? prop.maximum.toString() : '';
              property.validationRule = { type: 'range', value: `${min}-${max}` };
            } else if (prop['x-array-validation']) {
              // Parse custom array validation extensions
              const arrayValidation = prop['x-array-validation'];
              if (arrayValidation.type === 'all') {
                property.validationRule = {
                  type: 'array-all',
                  value: '',
                  elementType: arrayValidation.elementType || 'string',
                  values: arrayValidation.values ? arrayValidation.values.join(', ') : ''
                };
              } else if (arrayValidation.type === 'any') {
                property.validationRule = {
                  type: 'array-any',
                  value: '',
                  elementType: arrayValidation.elementType || 'string',
                  values: arrayValidation.values ? arrayValidation.values.join(', ') : ''
                };
              } else if (arrayValidation.type === 'length') {
                property.validationRule = {
                  type: 'array-length',
                  value: '',
                  min: arrayValidation.min ? arrayValidation.min.toString() : '',
                  max: arrayValidation.max ? arrayValidation.max.toString() : ''
                };
              }
            }
            
            const currentIndex = properties.length;
            properties.push(property);
            
            // Recursively parse nested objects
            if (prop.type === 'object' && prop.properties) {
              parseObject(prop, level + 1, currentIndex);
            }
          });
        }
      };
      
      parseObject(schema);
      return properties;
    } catch (e) {
      return [];
    }
  }, []);

  // Helper function to build custom validation rules from schema properties
  // This extracts validation rules from the visual editor properties
  const buildCustomRules = useCallback((properties: SchemaProperty[]): any[] => {
    const rules: any[] = [];
    
    properties.forEach((prop) => {
      if (prop.validationRule && prop.validationRule.type) {
        const rule: any = {
          field: prop.name,
          type: prop.validationRule.type
        };
        
        // Add additional fields based on validation type
        if (prop.validationRule.value) {
          rule.value = prop.validationRule.value;
        }
        if (prop.validationRule.elementType) {
          rule.elementType = prop.validationRule.elementType;
        }
        if (prop.validationRule.values) {
          rule.values = prop.validationRule.values;
        }
        if (prop.validationRule.min) {
          rule.min = prop.validationRule.min;
        }
        if (prop.validationRule.max) {
          rule.max = prop.validationRule.max;
        }
        
        rules.push(rule);
      }
    });
    
    return rules;
  }, []);

  // Create a wrapped setIsModified that checks if we're loading initial data
  const wrappedSetIsModified = useCallback((modified: boolean) => {
    if (!isLoadingInitialData.current) {
      setIsModified(modified);
    }
  }, []);

  // Initialize custom hooks for request and response schema management
  const requestSchema = useRequestSchema(wrappedSetIsModified, parseSchemaToProperties);
  const responseSchema = useResponseSchema(wrappedSetIsModified, parseSchemaToProperties);

  // Destructure commonly-used values from hooks for convenience
  const { 
    schemaJson, 
    setSchemaJson, 
    schemaProperties,
    setSchemaProperties,
    schemaJsonValid,
    schemaValidationErrors,
    metadataSchemaProperties,
    metadataSchemaJson,
    setMetadataSchemaJson,
    parseSchemaPreservingCollapsedState,
    buildSchemaFromProperties,
    buildMetadataJsonFromProperties,
    syncMetadataJsonToVisual,
  } = requestSchema;
  
  const { 
    responseSchemaJson, 
    setResponseSchemaJson,
    responseSchemaProperties,
    setResponseSchemaProperties,
    responseSchemaJsonValid,
    responseSchemaValidationErrors,
    responseMetadataSchemaJson,
    setResponseMetadataSchemaJson,
    parseResponseSchemaPreservingCollapsedState,
    buildResponseSchemaFromProperties,
    syncResponseMetadataJsonToVisual,
  } = responseSchema;

  // Helper function to validate JSON schema types

  // Schema change handler - validation is now handled internally by the custom hooks
  const handleSchemaChange = (value: string, isResponse: boolean = false, isMetadata: boolean = false) => {
    if (isMetadata) {
      if (isResponse) {
        setResponseMetadataSchemaJson(value);
      } else {
        setMetadataSchemaJson(value);
      }
    } else {
      if (isResponse) {
        setResponseSchemaJson(value);
      } else {
        setSchemaJson(value);
      }
    }
    wrappedSetIsModified(true);
  };

  const validateForm = React.useCallback(() => {
    const errors: string[] = [];
    
    // Validate required fields
    if (!formData.name.trim()) {
      errors.push('Channel name is required');
    }
    
    if (!formData.listen_address.trim()) {
      errors.push('Listen address is required');
    }
    
    // Validate endpoint based on type
    if (formData.endpoint_type === 'url') {
      if (!formData.target_endpoint.trim()) {
        errors.push('Target endpoint URL is required');
      } else if (!formData.target_endpoint.startsWith('http://') && 
                 !formData.target_endpoint.startsWith('https://') &&
                 !formData.target_endpoint.startsWith('proxy://')) {
        errors.push('Target endpoint must start with http://, https://, or proxy://');
      }
    } else if (formData.endpoint_type === 'mcp-proxy') {
      if (!formData.mcp_proxy_id) {
        errors.push('MCP proxy selection is required');
      }
    } else if (formData.endpoint_type === 'gateway') {
      if (!formData.gateway_id) {
        errors.push('Gateway selection is required');
      }
      if (!formData.gateway_channel) {
        errors.push('Gateway channel selection is required');
      }
    }
    
    // Check JSON validation states (don't re-parse, just check current state)
    if (!schemaJsonValid) {
      if (schemaValidationErrors.length > 0) {
        schemaValidationErrors.forEach(error => {
          if (!errors.includes(error)) errors.push(error);
        });
      } else {
        errors.push('Request schema JSON is invalid');
      }
    }
    if (!responseSchemaJsonValid) {
      if (responseSchemaValidationErrors.length > 0) {
        responseSchemaValidationErrors.forEach(error => {
          if (!errors.includes(error)) errors.push(error);
        });
      } else {
        errors.push('Response schema JSON is invalid');
      }
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  }, [formData.name, formData.listen_address, formData.target_endpoint, formData.endpoint_type, formData.gateway_id, formData.gateway_channel, schemaJsonValid, responseSchemaJsonValid, schemaValidationErrors, responseSchemaValidationErrors]);

  const handleInputChange = (field: keyof ChannelFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    wrappedSetIsModified(true);
    validateForm();
  };

  // Only revalidate form on schema changes if there were already errors showing
  // This prevents showing validation errors before the user tries to save
  useEffect(() => {
    if (isFormInitialized && validationErrors.length > 0) {
      validateForm();
    }
  }, [schemaValidationErrors, responseSchemaValidationErrors, schemaJsonValid, responseSchemaJsonValid, isFormInitialized, validateForm]);

  // Track last synced JSON to detect manual JSON changes
  const [lastSyncedSchemaJson, setLastSyncedSchemaJson] = useState<string>('');
  
  
  // Update visual editor when JSON changes
  useEffect(() => {
    if (schemaJson && schemaJson !== lastSyncedSchemaJson) {
      // Always auto-sync during initial form load, or when JSON is valid and different from last sync
      if (!isFormInitialized || schemaJsonValid) {
        try {
          const newProperties = isFormInitialized 
            ? parseSchemaPreservingCollapsedState(schemaJson) 
            : parseSchemaToProperties(schemaJson);
          setSchemaProperties(newProperties);
          setLastSyncedSchemaJson(schemaJson);
        } catch (e) {
          // If parsing fails, don't update the tracking so it can retry when JSON becomes valid
        }
      }
    }
  }, [schemaJson, lastSyncedSchemaJson, schemaJsonValid, isFormInitialized, parseSchemaToProperties, parseSchemaPreservingCollapsedState]);

  // Similar tracking for response schema
  const [lastSyncedResponseSchemaJson, setLastSyncedResponseSchemaJson] = useState<string>('');
  
  useEffect(() => {
    if (responseSchemaJson && responseSchemaJson !== lastSyncedResponseSchemaJson) {
      if (!isFormInitialized || responseSchemaJsonValid) {
        try {
          const newProperties = isFormInitialized 
            ? parseResponseSchemaPreservingCollapsedState(responseSchemaJson)
            : parseSchemaToProperties(responseSchemaJson);
          setResponseSchemaProperties(newProperties);
          setLastSyncedResponseSchemaJson(responseSchemaJson);
        } catch (e) {
          // Response auto-sync failed
        }
      }
    }
  }, [responseSchemaJson, lastSyncedResponseSchemaJson, responseSchemaJsonValid, isFormInitialized, parseSchemaToProperties, parseResponseSchemaPreservingCollapsedState]);

  // Similar tracking for metadata schema (custom_metadata injection)
  const [lastSyncedMetadataSchemaJson, setLastSyncedMetadataSchemaJson] = useState<string>('');
  
  useEffect(() => {
    if (metadataSchemaJson && metadataSchemaJson !== lastSyncedMetadataSchemaJson) {
      if (!isFormInitialized || requestSchema.metadataSchemaJsonValid) {
        try {
          syncMetadataJsonToVisual();
          setLastSyncedMetadataSchemaJson(metadataSchemaJson);
        } catch (e) {
          // Metadata auto-sync failed
        }
      }
    }
  }, [metadataSchemaJson, lastSyncedMetadataSchemaJson, requestSchema.metadataSchemaJsonValid, isFormInitialized, syncMetadataJsonToVisual]);

  // Similar tracking for response metadata schema
  const [lastSyncedResponseMetadataSchemaJson, setLastSyncedResponseMetadataSchemaJson] = useState<string>('');
  
  useEffect(() => {
    if (responseMetadataSchemaJson && responseMetadataSchemaJson !== lastSyncedResponseMetadataSchemaJson) {
      if (!isFormInitialized || responseSchema.responseMetadataJsonValid) {
        try {
          syncResponseMetadataJsonToVisual();
          setLastSyncedResponseMetadataSchemaJson(responseMetadataSchemaJson);
        } catch (e) {
          // Response metadata auto-sync failed
        }
      }
    }
  }, [responseMetadataSchemaJson, lastSyncedResponseMetadataSchemaJson, responseSchema.responseMetadataJsonValid, isFormInitialized, syncResponseMetadataJsonToVisual]);

  // Get identities for this channel

  // Extract x-identity field names from schema (without payload data)

  
  const handleSave = useCallback(async () => {
    // Store the currently focused element to refocus after save
    const activeElement = document.activeElement as HTMLElement;
    
    // Capture any pending input value from the focused element without blurring
    // Update properties arrays directly to ensure synchronous updates before save
    let updatedSchemaProperties = [...schemaProperties];
    let updatedResponseSchemaProperties = [...responseSchemaProperties];
    
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.tagName === 'SELECT')) {
      const inputElement = activeElement as HTMLInputElement;
      const currentValue = inputElement.value;
      
      // Find the property index and field type from the input's data attributes or context
      const propertyIndex = inputElement.getAttribute('data-property-index');
      const fieldType = inputElement.getAttribute('data-field-type');
      
      if (propertyIndex !== null && fieldType !== null) {
        const index = parseInt(propertyIndex, 10);
        
        // Update the appropriate property directly in the arrays
        switch (fieldType) {
          case 'name':
            if (updatedSchemaProperties[index]) {
              updatedSchemaProperties[index].name = currentValue;
            }
            break;
          case 'type':
            if (updatedSchemaProperties[index]) {
              updatedSchemaProperties[index].type = currentValue;
            }
            break;
          case 'validation-value':
            if (updatedSchemaProperties[index] && updatedSchemaProperties[index].validationRule) {
              updatedSchemaProperties[index].validationRule!.value = currentValue;
            }
            break;
          case 'validation-type':
            if (updatedSchemaProperties[index]) {
              if (!updatedSchemaProperties[index].validationRule) {
                updatedSchemaProperties[index].validationRule = { type: currentValue, value: '' };
              } else {
                updatedSchemaProperties[index].validationRule!.type = currentValue;
              }
            }
            break;
          case 'validation-element-type':
            if (updatedSchemaProperties[index] && updatedSchemaProperties[index].validationRule) {
              updatedSchemaProperties[index].validationRule!.elementType = currentValue;
            }
            break;
          case 'validation-values':
            if (updatedSchemaProperties[index] && updatedSchemaProperties[index].validationRule) {
              updatedSchemaProperties[index].validationRule!.values = currentValue;
            }
            break;
          case 'validation-min':
            if (updatedSchemaProperties[index] && updatedSchemaProperties[index].validationRule) {
              updatedSchemaProperties[index].validationRule!.min = currentValue;
            }
            break;
          case 'validation-max':
            if (updatedSchemaProperties[index] && updatedSchemaProperties[index].validationRule) {
              updatedSchemaProperties[index].validationRule!.max = currentValue;
            }
            break;
          // Response schema fields
          case 'response-name':
            if (updatedResponseSchemaProperties[index]) {
              updatedResponseSchemaProperties[index].name = currentValue;
            }
            break;
          case 'response-type':
            if (updatedResponseSchemaProperties[index]) {
              updatedResponseSchemaProperties[index].type = currentValue;
            }
            break;
          case 'response-validation-value':
            if (updatedResponseSchemaProperties[index] && updatedResponseSchemaProperties[index].validationRule) {
              updatedResponseSchemaProperties[index].validationRule!.value = currentValue;
            }
            break;
          case 'response-validation-type':
            if (updatedResponseSchemaProperties[index]) {
              if (!updatedResponseSchemaProperties[index].validationRule) {
                updatedResponseSchemaProperties[index].validationRule = { type: currentValue, value: '' };
              } else {
                updatedResponseSchemaProperties[index].validationRule!.type = currentValue;
              }
            }
            break;
          case 'response-validation-element-type':
            if (updatedResponseSchemaProperties[index] && updatedResponseSchemaProperties[index].validationRule) {
              updatedResponseSchemaProperties[index].validationRule!.elementType = currentValue;
            }
            break;
          case 'response-validation-values':
            if (updatedResponseSchemaProperties[index] && updatedResponseSchemaProperties[index].validationRule) {
              updatedResponseSchemaProperties[index].validationRule!.values = currentValue;
            }
            break;
          case 'response-validation-min':
            if (updatedResponseSchemaProperties[index] && updatedResponseSchemaProperties[index].validationRule) {
              updatedResponseSchemaProperties[index].validationRule!.min = currentValue;
            }
            break;
          case 'response-validation-max':
            if (updatedResponseSchemaProperties[index] && updatedResponseSchemaProperties[index].validationRule) {
              updatedResponseSchemaProperties[index].validationRule!.max = currentValue;
            }
            break;
        }
      }
    }
    
    // Force sync any pending UI changes before validation and save
    // Update the state with our captured changes first
    setSchemaProperties(updatedSchemaProperties);
    setResponseSchemaProperties(updatedResponseSchemaProperties);
    
    // Force JSON sync from visual properties (equivalent to what onBlur does)
    let updatedSchemaJson = schemaJson;
    let updatedResponseSchemaJson = responseSchemaJson;
    let updatedMetadataSchemaJson = metadataSchemaJson;
    
    if (updatedSchemaProperties.length > 0) {
      const jsonFromProps = buildSchemaFromProperties(updatedSchemaProperties);
      const jsonString = JSON.stringify(jsonFromProps, null, 2);
      updatedSchemaJson = jsonString;
      setSchemaJson(jsonString);
      setLastSyncedSchemaJson(jsonString);
    } else if (schemaJson.trim()) {
      // Keep existing JSON if no visual properties
      updatedSchemaJson = schemaJson;
    }
    
    if (updatedResponseSchemaProperties.length > 0) {
      const responseJsonFromProps = buildResponseSchemaFromProperties(updatedResponseSchemaProperties);
      const responseJsonString = JSON.stringify(responseJsonFromProps, null, 2);
      updatedResponseSchemaJson = responseJsonString;
      setResponseSchemaJson(responseJsonString);
      setLastSyncedResponseSchemaJson(responseJsonString);
    } else if (responseSchemaJson.trim()) {
      // Keep existing JSON if no visual properties
      updatedResponseSchemaJson = responseSchemaJson;
    }
    
    // Sync metadata JSON from visual properties
    if (metadataSchemaProperties.length > 0) {
      const metadataJsonFromProps = buildMetadataJsonFromProperties(metadataSchemaProperties);
      const metadataJsonString = JSON.stringify(metadataJsonFromProps, null, 2);
      updatedMetadataSchemaJson = metadataJsonString;
      setMetadataSchemaJson(metadataJsonString);
    } else if (metadataSchemaJson.trim()) {
      // Keep existing JSON if no visual properties
      updatedMetadataSchemaJson = metadataSchemaJson;
    }
    
    // Wait a moment for state updates to complete before validation
    await new Promise(resolve => setTimeout(resolve, 10));
    
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    setMessage(null);
    
    // Show loading feedback
    showChannelToast('loading', `Saving channel "${formData.name}"...`);
    
    try {
      // Strip protocol from selected address and construct route
      const hostPort = selectedHostPort.replace(/^https?:\/\//, '');
      const normalizedPath = customPath.startsWith('/') ? customPath : `/${customPath}`;
      // Remove leading slash from prefix to avoid double slashes
      const normalizedPrefix = selectedPrefix.startsWith('/') ? selectedPrefix.slice(1) : selectedPrefix;
      const route = `/${normalizedPrefix}${normalizedPath}`;
      
      // Prepare channel update data - include ALL original fields
      const updateData: any = {
        name: formData.name,
        config_id: formData.config_id,
        description: formData.description,
        listen_address: hostPort,
        route: route,
        target_endpoint: formData.target_endpoint,
        enabled: formData.enabled,
        enforce_extension: formData.enforce_extension,
        enforce_response_extension: formData.enforce_response_extension,
        protocol: formData.protocol || 'a2a', // Include protocol type
      };
      
      // Include fabric target name if it's a fabric endpoint
      if (formData.target_endpoint?.startsWith('fabric://')) {
        // First, try to preserve existing fabric_target_name from loaded channel
        const existingChannel = stats?.channels?.find(ch => ch.config_id === formData.config_id);
        let fabricTargetName = existingChannel?.fabric_target_name;
        
        // If we have gateway_channel selected, try to get fresh name from gatewayChannels
        if (formData.gateway_channel) {
          const targetChannel = gatewayChannels.find(ch => ch.config_id === formData.gateway_channel);
          if (targetChannel) {
            fabricTargetName = targetChannel.name;
          }
        }
        
        // Set fabric_target_name if we have a value (either existing or fresh)
        if (fabricTargetName) {
          updateData.fabric_target_name = fabricTargetName;
        }
      }
      
      // Always include extension_rules and response_extension_rules (even if empty)
      updateData.extension_rules = formData.extension_rules || {};
      updateData.response_extension_rules = formData.response_extension_rules || {};
      
      // Include extension fields (primary_extension and supported_extensions)
      if (formData.primary_extension) {
        updateData.primary_extension = formData.primary_extension;
      }
      if (formData.supported_extensions && formData.supported_extensions.length > 0) {
        updateData.supported_extensions = formData.supported_extensions;
      }
      
      // Include custom_metadata if enabled
      if (formData.custom_metadata?.enabled) {
        updateData.custom_metadata = {
          enabled: true,
          payload: undefined
        };
        
        if (updatedMetadataSchemaJson.trim() && updatedMetadataSchemaJson.trim() !== '{}') {
          try {
            const parsedMetadataSchema = JSON.parse(updatedMetadataSchemaJson);
            updateData.custom_metadata.payload = parsedMetadataSchema;
          } catch (e) {
            throw new Error('Invalid JSON in custom metadata');
          }
        }
      }
      
      // Include response_custom_metadata if enabled
      if (formData.response_custom_metadata?.enabled) {
        updateData.response_custom_metadata = {
          enabled: true,
          payload: undefined,
          injection_target: formData.response_custom_metadata.injection_target
        };
        
        if (responseMetadataSchemaJson.trim() && responseMetadataSchemaJson.trim() !== '{}') {
          try {
            const parsedResponseMetadataSchema = JSON.parse(responseMetadataSchemaJson);
            updateData.response_custom_metadata.payload = parsedResponseMetadataSchema;
          } catch (e) {
            throw new Error('Invalid JSON in response custom metadata');
          }
        }
      }
      
      // Include OPA policy config from formData
      if (formData.opa_policy_config?.enabled) {
        updateData.opa_enabled = formData.opa_policy_config.enabled;
        updateData.opa_policy = formData.opa_policy_config.policy;
      } else {
        updateData.opa_enabled = false;
        updateData.opa_policy = "";
      }
      
      // Include MCP identity extraction config if protocol is MCP
      if (formData.protocol === 'mcp') {
        updateData.mcp_identity_config = formData.mcp_identity_config;
        
        // Include MCP tool policies from formData
        updateData.mcp_tool_policies_enabled = formData.mcp_tool_policies_config?.enabled || false;
        updateData.mcp_tool_policies = formData.mcp_tool_policies_config?.enabled ? (formData.mcp_tool_policies_config.policies || []) : [];
      }
      
      // Include networking configs from formData
      updateData.rate_limit = formData.rate_limit || null;
      updateData.timeout = formData.timeout || null;
      updateData.retry = formData.retry || null;
      updateData.circuit_breaker = formData.circuit_breaker || null;
      updateData.mirror = formData.mirror || null;
      
      // Include DID document publishing flag
      updateData.publish_to_did_document = formData.publish_to_did_document || false;
      
      // Update schemas and custom rules if they exist
      if (updatedSchemaJson.trim()) {
        try {
          const parsedSchema = JSON.parse(updatedSchemaJson);
          // Parse the JSON to get the current properties for building custom rules
          const currentProperties = parseSchemaToProperties(updatedSchemaJson);
          const customRules = buildCustomRules(currentProperties);
          // For MCP protocol, automatically set extension_rules.required based on mcp_identity_config.enabled
          const requiredValue = (formData.protocol === 'mcp' || formData.protocol === 'mpx') 
            ? (formData.mcp_identity_config?.enabled || false)
            : (updateData.extension_rules?.required || false);
          updateData.extension_rules = {
            ...updateData.extension_rules,
            json_schema: parsedSchema,
            rules: customRules,
            required: requiredValue
          };
        } catch (e) {
          throw new Error('Invalid JSON in extension schema');
        }
      } else if (updatedSchemaProperties.length > 0) {
        // Build custom rules even if there's no schema JSON (visual editor only)
        const customRules = buildCustomRules(updatedSchemaProperties);
        if (customRules.length > 0) {
          // For MCP protocol, automatically set extension_rules.required based on mcp_identity_config.enabled
          const requiredValue = (formData.protocol === 'mcp' || formData.protocol === 'mpx') 
            ? (formData.mcp_identity_config?.enabled || false)
            : (updateData.extension_rules?.required || false);
          updateData.extension_rules = {
            ...updateData.extension_rules,
            rules: customRules,
            required: requiredValue
          };
        }
      }
      
      if (updatedResponseSchemaJson.trim()) {
        try {
          const parsedResponseSchema = JSON.parse(updatedResponseSchemaJson);
          // Parse the JSON to get the current properties for building custom rules
          const currentResponseProperties = parseSchemaToProperties(updatedResponseSchemaJson);
          const customResponseRules = buildCustomRules(currentResponseProperties);
          // For MCP protocol, automatically set response_extension_rules.required based on mcp_identity_config.enabled
          const requiredValue = (formData.protocol === 'mcp' || formData.protocol === 'mpx') 
            ? (formData.mcp_identity_config?.enabled || false)
            : (updateData.response_extension_rules?.required || false);
          updateData.response_extension_rules = {
            ...updateData.response_extension_rules,
            json_schema: parsedResponseSchema,
            rules: customResponseRules,
            required: requiredValue
          };
        } catch (e) {
          throw new Error('Invalid JSON in response schema');
        }
      } else if (updatedResponseSchemaProperties.length > 0) {
        // Build custom rules even if there's no response schema JSON (visual editor only)
        const customResponseRules = buildCustomRules(updatedResponseSchemaProperties);
        if (customResponseRules.length > 0) {
          // For MCP protocol, automatically set response_extension_rules.required based on mcp_identity_config.enabled
          const requiredValue = (formData.protocol === 'mcp' || formData.protocol === 'mpx') 
            ? (formData.mcp_identity_config?.enabled || false)
            : (updateData.response_extension_rules?.required || false);
          updateData.response_extension_rules = {
            ...updateData.response_extension_rules,
            rules: customResponseRules,
            required: requiredValue
          };
        }
      }
      
      // Debug: Log the complete updateData object being saved
      // console.log('[EditChannelPage] Complete updateData object:', JSON.stringify(updateData, null, 2));
      // console.log('[EditChannelPage] Networking state variables:', {
      //   timeoutEnabled,
      //   retryEnabled,
      //   circuitBreakerEnabled,
      //   mirrorEnabled,
      //   timeout: { timeoutRequest, timeoutConnect, timeoutIdle },
      //   retry: { retryMaxAttempts, retryInitialBackoff, retryMaxBackoff, retryBackoffMultiplier, retryStatusCodes },
      //   circuitBreaker: { cbFailureThreshold, cbSuccessThreshold, cbTimeout, cbWindow },
      //   mirror: { mirrorEndpoint, mirrorPercentage, mirrorWaitForResponse, mirrorTimeout }
      // });
      
      // Save via API - use createChannel for new channels, updateChannel for existing ones
      if (isCreateMode) {
        await apiClient.createChannel(updateData);
        // Add the new channel to the app state immediately
        actions.addChannel({
          ...updateData,
          last_status: null, // New channels start with unknown status
          total_connections: 0,
          active_connections: 0,
          bytes_sent: 0,
          bytes_received: 0,
          last_activity: null
        });
      } else {
        await apiClient.updateChannel(formData.config_id, updateData);
        // Update the existing channel in the app state
        actions.updateChannel(updateData);
      }
      
      // Only clear dirty state and validation errors on successful save
      setIsModified(false);
      setValidationErrors([]);
      
      if (isCreateMode) {
        showChannelToast('success', `Channel "${formData.name}" has been successfully created.`);
        // Navigate back to channels list after successful creation
        setTimeout(() => navigate('/channels'), 1000);
      } else {
        showChannelToast('success', `Channel "${formData.name}" has been successfully saved.`);
        // Element should still be focused since we didn't blur it
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save channel';
      showChannelToast('error', errorMessage);
      setValidationErrors([errorMessage]);
      // Keep form marked as dirty (don't call setIsModified(false)) so save can be re-attempted
      // Element should still be focused since we didn't blur it
    } finally {
      setIsSaving(false);
    }
  }, [formData, schemaJson, responseSchemaJson, schemaProperties, responseSchemaProperties, validateForm, isCreateMode, navigate, actions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = useCallback(async () => {
    if (!formData.config_id || isCreateMode) {
      return;
    }
    
    setIsSaving(true);
    showChannelToast('loading', `Deleting channel "${formData.name}"...`);
    
    try {
      await apiClient.deleteChannel(formData.config_id);
      showChannelToast('success', `Channel "${formData.name}" has been successfully deleted.`);
      
      // Remove the channel from the app state immediately
      actions.removeChannel(formData.config_id);
      
      // Navigate back to channels list after successful deletion
      setTimeout(() => navigate('/channels'), 1000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete channel';
      showChannelToast('error', errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [formData.config_id, formData.name, isCreateMode, navigate, actions]);

  const handleReload = async () => {
    if (configId) {
      const currentChannel = stats?.channels?.find(r => r.config_id === configId);
      const channelName = currentChannel?.name || configId;
      
      setIsReloading(true);
      setMessage(null);
      
      // Show loading feedback using direct DOM manipulation
      showChannelToast('loading', `Reloading channel "${channelName}"...`);
      
      try {
        // Reload channel configuration from server using config_id (not name)
        await apiClient.reloadChannel(configId);
        setValidationErrors([]);
        
        // Force form to reload by resetting initialization flags
        // This will trigger the useEffect to reload channel data from stats
        setIsFormInitialized(false);
        setIsModified(false);
        
        // Show success toast directly to DOM
        showChannelToast('success', `Channel "${channelName}" has been successfully reloaded from the server.`);
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to reload channel';
        showChannelToast('error', errorMessage);
        setValidationErrors([errorMessage]);
      } finally {
        setIsReloading(false);
      }
    }
  };

  const handleCancel = () => {
    if (isModified) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        navigate('/channels');
      }
    } else {
      navigate('/channels');
    }
  };

  // Add keyboard shortcut support for save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyboardSave = (event: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault(); // Prevent browser's default save behavior
        
        // Only trigger save if there are changes and no validation errors
        if (isModified && validationErrors.length === 0 && !isSaving) {
          // Trigger the same save handler as the button
          handleSave();
        }
      }
    };

    // Add event listener when component mounts
    document.addEventListener('keydown', handleKeyboardSave);

    // Cleanup: remove event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyboardSave);
    };
  }, [isModified, validationErrors, isSaving, handleSave]); // Dependencies for the effect

  // Show protocol selection screen if in create mode and protocol not yet selected
  if (isCreateMode && !protocolSelected) {
    return (
      <div className="container-fluid">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800">
            <i className="fas fa-plus-circle mr-2"></i>
            Create New Channel
          </h1>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/channels')}
          >
            <i className="fas fa-times mr-1"></i>
            Cancel
          </button>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="welcome-screen">
              <h2>Select Channel Protocol</h2>
              <p>
                Choose the protocol type for your new channel. This determines how the channel 
                will communicate and cannot be changed after creation.
              </p>

              <div className="protocol-selection">
                <button
                  className="protocol-button"
                  onClick={() => handleProtocolSelect('a2a')}
                >
                  <div className="protocol-icon">
                    <i className="fas fa-exchange-alt"></i>
                  </div>
                  <div className="protocol-name">A2A Protocol</div>
                  <div className="protocol-description">
                    Agent-to-Agent communication using DIDComm messaging. 
                    Perfect for secure, identity-based agent interactions.
                  </div>
                </button>

                <button
                  className="protocol-button"
                  onClick={() => handleProtocolSelect('ap2')}
                >
                  <div className="protocol-icon">
                    <i className="fas fa-layer-group"></i>
                  </div>
                  <div className="protocol-name">AP2 Protocol</div>
                  <div className="protocol-description">
                  The Agent Payments Protocol (AP2) is an extension of A2A specifically designed to enable secure, auditable, and payment-agnostic financial transactions initiated by AI agents.
                  </div>
                </button>

                <button
                  className="protocol-button"
                  onClick={() => handleProtocolSelect('mcp')}
                >
                  <div className="protocol-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="protocol-name">MCP Protocol</div>
                  <div className="protocol-description">
                    Model Context Protocol for AI model communication. 
                    Built on JSON-RPC 2.0 for tools, resources, and prompts.
                  </div>
                </button>

                <button
                  className="protocol-button"
                  onClick={() => handleProtocolSelect('mpx')}
                >
                  <div className="protocol-icon">
                    <i className="fas fa-network-wired"></i>
                  </div>
                  <div className="protocol-name">MPX Protocol</div>
                  <div className="protocol-description">
                    Meeting Place brings privacy-preserving discovery and messaging between humans and agents.
                    Flexible architecture for complex messaging scenarios
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow mb-4 channel-editor-card">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className={`fas fa-${isCreateMode ? 'plus' : 'edit'}`}></i> {isCreateMode ? `Create New ${formData.protocol?.toUpperCase()} Channel` : `Edit Channel: ${formData.name}`}
            {!isCreateMode && !formData.enabled && (
              <span className="badge badge-warning ml-2">
                <i className="fas fa-power-off"></i> DISABLED
              </span>
            )}
            {!isCreateMode && channel?.channel_type === 'onboarding' && (
              <span className="badge badge-info ml-2">
                <i className="fas fa-user-plus"></i> ONBOARDING
              </span>
            )}
            {!isCreateMode && channel?.channel_type === 'system' && (
              <span className="badge badge-secondary ml-2">
                <i className="fas fa-cog"></i> SYSTEM
              </span>
            )}
            {!isCreateMode && channel?.channel_type === 'transit' && (
              <span className="badge badge-warning ml-2">
                <i className="fas fa-exchange-alt"></i> TRANSIT
              </span>
            )}
            {!isCreateMode && channel?.protocol && (
              <span className={`badge ml-2 ${isUcpChannel(channel) ? 'badge-info' : channel.protocol === 'a2a' ? 'badge-primary' : 'badge-success'}`}>
                <i className="fas fa-plug"> </i> {getDisplayProtocol(channel)} Protocol
              </span>
            )}
            {!isCreateMode && channel?.last_status && channel.last_status !== 'success' && (
              <span className={`badge ml-2 ${
                channel.last_status === 'fault' || channel.last_status === 'gateway_fault' ? 'badge-danger' : 'badge-warning'
              }`}>
                <i className="fas fa-exclamation-triangle"></i> {channel.last_status.toUpperCase().replace('_', ' ')}
              </span>
            )}
            {isCreateMode && formData.protocol && (
              <span className={`badge ml-2 ${isUcpChannel(formData) ? 'badge-info' : formData.protocol === 'a2a' ? 'badge-primary' : 'badge-success'}`}>
                <i className="fas fa-plug"> </i> {getDisplayProtocol(formData)} Protocol
              </span>
            )}
          </h6>
          <div>
            <button 
              className={`btn btn-sm btn-primary mr-2 ${isSaving ? 'disabled' : ''}`}
              onClick={handleSave}
              disabled={(!isModified && !isCreateMode) || validationErrors.length > 0 || isSaving}
            >
              <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i> 
              {isSaving ? ' Saving...' : (isCreateMode ? ' New' : ' Save')}
            </button>
            {!isCreateMode && (
              <button 
                className={`btn btn-sm btn-outline-info mr-2 ${isReloading ? 'disabled' : ''}`}
                onClick={handleReload}
                disabled={isReloading}
                title="Reload channel from server"
              >
                <i className={`fas ${isReloading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i> 
                {isReloading ? ' Reloading...' : ' Reload'}
              </button>
            )}
            {!isCreateMode && (
              <button 
                className="btn btn-sm btn-outline-secondary mr-2"
                onClick={() => navigate(`/channels/${encodeURIComponent(configId!)}/capture`)}
                title="Capture live agent payloads for this channel"
              >
                <i className="fas fa-eye"></i> Capture
              </button>
            )}            {!isCreateMode && (
              <DeleteButton
                onDelete={handleDelete}
                className="mr-2"
                title="Delete this channel"
                disabled={isSaving}
                variant="danger"
              > Delete
              </DeleteButton>
            )}
            <button className="btn btn-sm btn-secondary" onClick={handleCancel}>
              <i className="fas fa-times"></i> Close
            </button>
          </div>
        </div>
        
        <div className="card-body-channel-editor">
          {/* Success/Error Message - Fixed position to ensure visibility */}
          {message && (
            <div 
              key={`${message.type}-${Date.now()}`} 
              className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-4`}
              style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 9999,
                minWidth: '400px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: '2px solid ' + (message.type === 'success' ? '#151615ff' : '#dc3545'),
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
              <strong>{message.type === 'success' ? 'Success!' : 'Error!'}</strong> {message.text}
              <button type="button" className="close" onClick={() => setMessage(null)} title="Close notification">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          )}

          {/* Validation Messages */}
          {validationErrors.length > 0 && (
            <div className="alert alert-danger mb-4">
              <strong>Validation Errors:</strong>
              <ul className="mb-0 mt-2">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="mb-3 custom-channel-tabs"
          >
            {/* Overview Tab */}
            <Tab eventKey="glance" title={<><i className="fas fa-info-circle"></i> Overview</>}>
              {channel ? (
                <OverviewTab
                  channel={channel}
                  formData={formData}
                  fullRouteDisplay={fullRouteDisplay}
                  gateways={gateways}
                  gatewayChannels={gatewayChannels}
                  channelNameCache={channelNameCache}
                  mediators={mediators}
                  mpxTenants={mpxTenants}
                  handleInputChange={handleInputChange}
                />
              ) : (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading channel data...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading channel overview...</p>
                </div>
              )}
            </Tab>
            
            {/* Basic Information Tab */}
            <Tab eventKey="basic" title={<><i className="fas fa-route"></i> Routing</>}>
              <RoutingTab
                formData={formData}
                handleInputChange={handleInputChange}
                setFormData={setFormData}
                setIsModified={setIsModified}
                setValidationErrors={setValidationErrors}
                routingConfig={routingConfig}
                selectedHostPort={selectedHostPort}
                setSelectedHostPort={setSelectedHostPort}
                selectedPrefix={selectedPrefix}
                setSelectedPrefix={setSelectedPrefix}
                customPath={customPath}
                setCustomPath={setCustomPath}
                isCreateMode={isCreateMode}
                gateways={gateways}
                gatewayChannels={gatewayChannels}
                isLoadingChannels={isLoadingChannels}
                setGatewayChannels={setGatewayChannels}
                loadedGatewayRef={loadedGatewayRef}
                refreshGatewayChannelsCache={refreshGatewayChannelsCache}
                isRefreshingChannels={isRefreshingChannels}
                fullRouteDisplay={fullRouteDisplay}
                channelNameCache={channelNameCache}
                channel={channel}
              />
            </Tab>

            {/* Extension Rules (Request) Tab */}
            <Tab eventKey="request" title={<><i className="fas fa-arrow-right"></i> Inbound</>}>
              <InboundTab
                formData={formData}
                setFormData={setFormData}
                setIsModified={setIsModified}
                secrets={secrets}
                mediators={mediators}
                openSecretsDropdown={openSecretsDropdown}
                setOpenSecretsDropdown={setOpenSecretsDropdown}
                handleInputChange={handleInputChange}
                handleSchemaChange={handleSchemaChange}
                requestSchema={requestSchema}
              />
            </Tab>

            {/* Extension Rules (Response) Tab */}
            <Tab eventKey="response" title={<><i className="fas fa-arrow-left"></i> Outbound</>}>
              <OutboundTab
                formData={formData}
                setFormData={setFormData}
                setIsModified={setIsModified}
                secrets={secrets}
                openSecretsDropdown={openSecretsDropdown}
                setOpenSecretsDropdown={setOpenSecretsDropdown}
                handleInputChange={handleInputChange}
                handleSchemaChange={handleSchemaChange}
                responseSchema={responseSchema}
              />
            </Tab>

            {/* Monitoring Tab */}
            {!isCreateMode && (
            <Tab eventKey="monitoring" title={<><i className="fas fa-chart-line"></i> Monitoring</>}>
              <MonitoringTab 
                channelMetrics={channelMetrics}
                formData={{ config_id: formData.config_id, name: formData.name }}
                dashboardStats={stats}
                state={state}
                actions={actions}
              />
            </Tab>
            )}

            {/* Identities Tab */}
            <Tab eventKey="identities" title={<><i className="fas fa-users"></i> Identities</>}>
              {channel ? (
                <IdentitiesTab
                  formData={formData}
                  channel={channel}
                  dashboardStats={stats}
                />
              ) : (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading identity data...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading channel identities...</p>
                </div>
              )}
            </Tab>

            {/* Security & Policy Tab */}
            <Tab eventKey="security" title={<><i className="fas fa-shield-alt"></i> Policies</>}>
              <PoliciesTab
                formData={formData}
                setFormData={setFormData}
                setIsModified={setIsModified}
                opaPolicyValid={opaPolicyValid}
                opaPolicyErrors={opaPolicyErrors}
                isValidatingPolicy={isValidatingPolicy}
                editingPolicyId={editingPolicyId}
                setEditingPolicyId={setEditingPolicyId}
                showPolicyEditor={showPolicyEditor}
                setShowPolicyEditor={setShowPolicyEditor}
                policyName={policyName}
                setPolicyName={setPolicyName}
                policyDescription={policyDescription}
                setPolicyDescription={setPolicyDescription}
                policyCode={policyCode}
                setPolicyCode={setPolicyCode}
                policyEnforce={policyEnforce}
                setPolicyEnforce={setPolicyEnforce}
                policyPriority={policyPriority}
                setPolicyPriority={setPolicyPriority}
                isValidatingToolPolicy={isValidatingToolPolicy}
                toolPolicyValid={toolPolicyValid}
                toolPolicyErrors={toolPolicyErrors}
              />
            </Tab>
            
            {/* Networking / Traffic Management Tab */}
            <Tab eventKey="networking" title={<><i className="fas fa-network-wired"></i> Networking</>}>
              <NetworkingTab
                formData={formData}
                setFormData={setFormData}
                setIsModified={setIsModified}
              />
            </Tab>

            {/* Sandbox Tab - Only for MCP protocol channels */}
            {formData.protocol === 'mcp' && !isCreateMode && formData.enabled && channel?.route && (
              <Tab eventKey="sandbox" title={<><i className="fas fa-flask"></i> MCP Sandbox</>}>
                <McpSandbox
                  endpointUrl={channel.route}
                  endpointName={formData.name}
                  endpointDescription={formData.description}
                />
              </Tab>
            )}

            {/* MPX Sandbox Tab - Only for MPX protocol channels */}
            {formData.protocol === 'mpx' && !isCreateMode && formData.enabled && (
              <Tab eventKey="mpx-sandbox" title={<><i className="fas fa-flask"></i> MPX Sandbox</>}>
                <div className="card shadow">
                  <div className="card-body">
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-flask fa-3x mb-3" style={{opacity: 0.3}}></i>
                      <p>MPX Sandbox coming soon...</p>
                    </div>
                  </div>
                </div>
              </Tab>
            )}
          </Tabs>

        </div>
      </div>

      {/* Identity Details Modal */}
      {showIdentityModal && selectedIdentity && (
        <IdentityDetailsModal 
          identity={selectedIdentity}
          onClose={() => setShowIdentityModal(false)}
        />
      )}
    </div>
  );
};

export default EditChannelPage;
